// frontend/src/features/campaign/utils/campaignUtils.js

export function getStatusBadgeClass(status) {
  switch (status) {
    case 'ACTIVE':
      return 'cmp-badge-active';
    case 'APPROVED':
      return 'cmp-badge-approved';
    case 'PENDING_APPROVAL':
      return 'cmp-badge-pending';
    case 'CHANGES_REQUESTED':
      return 'cmp-badge-changes-requested';
    case 'DRAFT':
      return 'cmp-badge-draft';
    case 'PAUSED':
      return 'cmp-badge-paused';
    default:
      return 'cmp-badge-draft';
  }
}

export function getStatusLabel(status) {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'APPROVED':
      return 'Approved';
    case 'PENDING_APPROVAL':
      return 'Pending Approval';
    case 'CHANGES_REQUESTED':
      return 'Changes Requested';
    case 'DRAFT':
      return 'Draft';
    case 'PAUSED':
      return 'Paused';
    default:
      return status;
  }
}

export function getChannelIcon(channel = '') {
  const ch = channel.toLowerCase();
  if (ch.includes('linkedin')) return 'fab fa-linkedin-in';
  if (ch.includes('whatsapp')) return 'fab fa-whatsapp';
  if (ch.includes('telegram')) return 'fab fa-telegram-plane';
  if (ch.includes('phone') || ch.includes('call')) return 'fas fa-phone-alt';
  return 'fas fa-envelope';
}

export function getStageClass(stage) {
  switch (stage) {
    case 'Won':
      return 'stage-won';
    case 'Proposal':
      return 'stage-proposal';
    case 'Qualified':
      return 'stage-qualified';
    case 'Engaged':
      return 'stage-engaged';
    case 'Contacted':
      return 'stage-contacted';
    default:
      return 'stage-new';
  }
}

export function getScoreClass(score) {
  if (score >= 80) return 'score-high';
  if (score >= 60) return 'score-med';
  return 'score-low';
}

/**
 * Role hierarchy tier calculator:
 * Level 3 (Highest): Workspace Owner / Superadmin / Platform Admin
 * Level 2: Organization Admin / Admin
 * Level 1: Sales Manager / Manager
 * Level 0: Standard Member / Researcher / Viewer
 */
export function getRoleTier(roleStr = '') {
  const r = String(roleStr || '').toLowerCase();
  if (r.includes('owner') || r.includes('superadmin') || r.includes('platform')) {
    return 3;
  }
  if (r.includes('admin')) {
    return 2;
  }
  if (r.includes('manager')) {
    return 1;
  }
  return 0;
}

/**
 * Determines whether a user has authority to approve a pause request based on permissions and role hierarchy:
 * - Sales Manager / Admin / Owner with approval authority (canDirectApprove) can approve pause requests.
 * - Subordinate (lower tier e.g. Member over Manager, Manager over Admin/Owner) cannot override higher tier.
 */
export function checkPauseApprovalRequirement({
  campaign,
  currentUserName,
  userRoleLabel,
  canDirectApprove,
}) {
  if (!canDirectApprove) {
    return {
      canApprove: false,
      reason: 'Standard members cannot approve pause requests.',
    };
  }

  const rawApprovedBy = campaign?.approvedBy || campaign?.governance?.signedBy || '';

  const cleanOriginalSignatoryName = (rawApprovedBy || '').replace(/\(.*?\)/g, '').trim();
  const extractedRoleMatch = (String(rawApprovedBy).match(/\((.*?)\)/) || [])[1];
  const originalSignatoryRole =
    extractedRoleMatch ||
    campaign?.governance?.approverRole ||
    campaign?.approverRole ||
    'Sales Manager';

  const signatoryTier = getRoleTier(originalSignatoryRole);
  const currentTier = getRoleTier(userRoleLabel);

  // Lower tier cannot approve or override a higher tier's certified campaign
  if (currentTier < signatoryTier) {
    return {
      canApprove: false,
      originalSignatoryRole,
      cleanOriginalSignatoryName,
      reason: `Insufficient authority: ${userRoleLabel} cannot override or pause a campaign certified by ${originalSignatoryRole} (${cleanOriginalSignatoryName}). Higher executive approval is required.`,
    };
  }

  return {
    canApprove: true,
    originalSignatoryRole,
    cleanOriginalSignatoryName,
  };
}

/**
 * Normalizes lead properties across heterogeneous database/CRM schemas:
 * - handles `full_name`, `contact_name`, `crm.name`, `crm_meta.name`, or `name`
 * - handles single string or array of emails (`email`, `emails`, `email_addresses`, `crm.email`, `crm_meta.email`)
 * - handles single string or array of phones (`phone`, `phone_number`, `phones`, `phone_numbers`, `crm.phone`)
 * - handles company names (`company`, `company_name`, `companyName`, `company_details.companyName`, `organization`)
 * - handles job titles (`jobTitle`, `job_title`, `title`, `role`, `position`)
 * - handles locations (`country`, `location.country`, `location.raw_location`, `location`)
 * - handles scores (`leadScore`, `lead_score`, `score`)
 * - handles stages (`stage`, `crm.stage`, `crm_meta.stage`)
 * - handles linkedIn / social URLs
 */
export function normalizeLead(lead = {}, index = 0) {
  if (!lead || typeof lead !== 'object') {
    return {
      id: `lead_${index + 1}`,
      name: `Lead #${index + 1}`,
      jobTitle: 'Executive Decision Maker',
      company: 'Enterprise Co',
      industry: 'Enterprise SaaS',
      country: 'Malaysia',
      stage: 'New',
      leadScore: 75,
      email: '',
      phone: '',
      linkedinUrl: '',
    };
  }

  const crm = lead.crm || lead.crm_meta || {};

  // Name resolution
  const name =
    lead.name ||
    lead.full_name ||
    lead.fullName ||
    lead.contact_name ||
    lead.contactName ||
    crm.name ||
    (lead.first_name || lead.last_name
      ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
      : '') ||
    (lead.firstName || lead.lastName
      ? `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
      : '') ||
    `Lead #${index + 1}`;

  // Email resolution
  let rawEmail =
    lead.email ||
    crm.email ||
    (Array.isArray(lead.email_addresses) ? lead.email_addresses[0] : null) ||
    (Array.isArray(lead.emails) ? lead.emails[0] : null) ||
    (Array.isArray(crm.emails) ? crm.emails[0] : null) ||
    lead.contact_email ||
    lead.work_email ||
    '';

  let emailStr = '';
  if (typeof rawEmail === 'string') {
    emailStr = rawEmail.trim();
  } else if (Array.isArray(rawEmail)) {
    const first = rawEmail[0];
    emailStr = typeof first === 'string' ? first : first?.email || first?.value || '';
  } else if (rawEmail && typeof rawEmail === 'object') {
    emailStr = rawEmail.email || rawEmail.value || '';
  }

  // Phone resolution
  let rawPhone =
    lead.phone ||
    lead.phone_number ||
    crm.phone ||
    (Array.isArray(lead.phone_numbers) ? lead.phone_numbers[0] : null) ||
    (Array.isArray(lead.phones) ? lead.phones[0] : null) ||
    (Array.isArray(crm.phones) ? crm.phones[0] : null) ||
    lead.contact_phone ||
    lead.work_phone ||
    lead.mobile ||
    '';

  let phoneStr = '';
  if (typeof rawPhone === 'string') {
    phoneStr = rawPhone.trim();
  } else if (Array.isArray(rawPhone)) {
    const first = rawPhone[0];
    phoneStr =
      typeof first === 'string' ? first : first?.number || first?.phone || first?.value || '';
  } else if (rawPhone && typeof rawPhone === 'object') {
    phoneStr = rawPhone.number || rawPhone.phone || rawPhone.value || '';
  }

  // Job Title resolution
  const jobTitle =
    lead.jobTitle ||
    lead.job_title ||
    lead.title ||
    lead.role ||
    lead.position ||
    crm.jobTitle ||
    crm.title ||
    'Executive Decision Maker';

  // Company resolution
  const company =
    lead.company ||
    lead.company_name ||
    lead.companyName ||
    lead.company_details?.companyName ||
    lead.company_details?.name ||
    lead.organization ||
    lead.org_name ||
    crm.company ||
    crm.companyName ||
    'Enterprise Co';

  // Industry resolution
  const industry =
    lead.industry ||
    lead.company_industry ||
    lead.company_details?.mainIndustry ||
    crm.industry ||
    'Enterprise SaaS';

  // Country / Location resolution
  let countryStr = 'Malaysia';
  if (typeof lead.country === 'string' && lead.country.trim()) {
    countryStr = lead.country.trim();
  } else if (typeof lead.location === 'string' && lead.location.trim()) {
    countryStr = lead.location.trim();
  } else if (lead.location?.country) {
    countryStr = lead.location.country;
  } else if (lead.location?.raw_location) {
    countryStr = lead.location.raw_location;
  } else if (crm.country) {
    countryStr = crm.country;
  }

  // Stage resolution
  const stage = lead.stage || crm.stage || 'New';

  // Lead score resolution
  const rawScore =
    lead.leadScore ?? lead.lead_score ?? lead.score ?? crm.leadScore ?? crm.lead_score ?? 75;
  const parsedScore = typeof rawScore === 'number' ? rawScore : parseInt(rawScore, 10);
  const leadScore = isNaN(parsedScore) ? 75 : parsedScore;

  // LinkedIn resolution
  const linkedinUrl =
    lead.linkedinUrl ||
    lead.linkedin_url ||
    lead.social?.linkedin ||
    lead.social_links?.linkedin ||
    crm.linkedinUrl ||
    '';

  // ID resolution
  const id = String(
    lead.id || lead.shared_item_id || lead.prospect_id || lead.lead_id || `lead_${index + 1}`
  );

  return {
    ...lead,
    id,
    name,
    jobTitle,
    company,
    industry,
    country: countryStr,
    stage,
    leadScore,
    email: emailStr,
    phone: phoneStr,
    linkedinUrl,
  };
}

/**
 * Pure JavaScript NIST SHA-256 implementation with UTF-8 support
 */
export function calculateSha256(inputStr = '') {
  function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
  }

  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  const lengthProperty = 'length';
  let i, j;
  let result = '';

  const words = [];
  // Encode UTF-8 cleanly
  const utf8Str = unescape(encodeURIComponent(inputStr || ''));
  const asciiBitLength = utf8Str[lengthProperty] * 8;

  const hash = [];
  const k = [];
  let primeCounter = 0;

  const isComposite = {};
  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (i = 0; i < 300; i += candidate) {
        isComposite[i] = candidate;
      }
      if (primeCounter < 8) {
        hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      }
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }

  let ascii = utf8Str + '\x80';
  while ((ascii[lengthProperty] % 64) - 56) ascii += '\x00';
  for (i = 0; i < ascii[lengthProperty]; i++) {
    j = ascii.charCodeAt(i);
    words[i >> 2] |= j << (((3 - i) % 4) * 8);
  }
  words[words[lengthProperty]] = (asciiBitLength / maxWord) | 0;
  words[words[lengthProperty]] = asciiBitLength;

  for (j = 0; j < words[lengthProperty];) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0, 8);

    for (i = 0; i < 64; i++) {
      const w15 = w[i - 15] || 0;
      const w2 = w[i - 2] || 0;

      const a = hash[0];
      const e = hash[4];
      const s1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
      const ch = (e & hash[5]) ^ (~e & hash[6]);
      const temp1 =
        hash[7] +
        s1 +
        ch +
        k[i] +
        (w[i] =
          i < 16
            ? w[i] || 0
            : ((w[i - 16] || 0) +
                (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                (w[i - 7] || 0) +
                (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
              0);
      const s0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
      const maj = (a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]);
      const temp2 = s0 + maj;

      hash[7] = hash[6];
      hash[6] = hash[5];
      hash[5] = hash[4];
      hash[4] = (hash[3] + temp1) | 0;
      hash[3] = hash[2];
      hash[2] = hash[1];
      hash[1] = hash[0];
      hash[0] = (temp1 + temp2) | 0;
    }

    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }

  for (i = 0; i < 8; i++) {
    for (j = 3; j >= 0; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return 'sha256:' + result;
}

/**
 * Computes deterministic cryptographic hash of a campaign configuration
 */
export function computeCampaignGovernanceHash(data, customGates = null) {
  if (!data) return 'sha256:0000000000000000000000000000000000000000000000000000000000000000';

  const name = String(data.name || data.title || '').trim();
  const objective = String(data.objective || data.primaryObjective || '').trim();
  const product = String(data.product || '').trim();
  const campaignType = String(data.campaignType || '').trim();
  const industrySector = String(data.industrySector || '').trim();
  const targetMarket = String(data.targetMarket || data.targetRegion || '').trim();
  const budget = String(data.budget || data.allocatedBudget || '').trim();

  // Normalize personas
  const rawPersonas = data.targetPersonas || [];
  const targetPersonas = Array.from(new Set(rawPersonas.map((p) => String(p || '').trim()))).sort();

  // Normalize channels
  const rawChannels = data.channels || [];
  const channels = Array.from(new Set(rawChannels.map((c) => String(c || '').trim()))).sort();

  // Normalize matrix
  const rawMatrix = data.matrix || data.strategy?.matrix || [];
  const matrix = rawMatrix
    .map((m) => ({
      persona: String(m.persona || '').trim(),
      painPoint: String(m.painPoint || '').trim(),
      valueProp: String(m.valueProp || '').trim(),
      proofPoint: String(m.proofPoint || '').trim(),
      cta: String(m.cta || '').trim(),
      objection: String(m.objection || '').trim(),
    }))
    .sort((a, b) => a.persona.localeCompare(b.persona));

  // Normalize journey steps
  const rawJourney = data.journey || data.strategy?.journey || [];
  const journey = rawJourney.map((j, idx) => ({
    step: j.step ?? idx + 1,
    day: j.day ?? idx * 3 + 1,
    channel: String(j.channel || '').trim(),
    name: String(j.name || '').trim(),
  }));

  // Normalize imported leads / target cohort
  const rawLeads = data.importedLeads || data.targetLeads || data.strategy?.importedLeads || [];
  const leads = rawLeads
    .map((l) => ({
      id: String(l.id || ''),
      name: String(l.name || '').trim(),
      jobTitle: String(l.jobTitle || l.title || '').trim(),
      company: String(l.company || '').trim(),
    }))
    .sort((a, b) => (a.id || a.name).localeCompare(b.id || b.name));

  // Gates
  const rawGates = customGates || data.governance?.gates || data.approvedGates || [];
  const gates = Array.from(new Set(Array.from(rawGates))).sort();

  const canonicalPayload = {
    name,
    objective,
    product,
    campaignType,
    industrySector,
    targetMarket,
    budget,
    targetPersonas,
    channels,
    matrix,
    journey,
    leads,
    gates,
  };

  return calculateSha256(JSON.stringify(canonicalPayload));
}

/**
 * Detects specific modified fields between certified baseline and current campaign state
 */
export function detectCampaignModifications(certifiedData, currentData, customGates = null) {
  if (!certifiedData || !currentData) return [];
  const diffs = [];

  // Tab 1 checks
  const name1 = String(certifiedData.name || certifiedData.title || '').trim();
  const name2 = String(currentData.name || currentData.title || '').trim();
  if (name1 && name2 && name1 !== name2) {
    diffs.push({
      tab: 'Tab 1: Brief & Goals',
      field: 'Campaign Title',
      before: name1,
      after: name2,
    });
  }

  const obj1 = String(certifiedData.objective || certifiedData.primaryObjective || '').trim();
  const obj2 = String(currentData.objective || currentData.primaryObjective || '').trim();
  if (obj1 && obj2 && obj1 !== obj2) {
    diffs.push({
      tab: 'Tab 1: Brief & Goals',
      field: 'Primary Objective',
      before: obj1,
      after: obj2,
    });
  }

  const bud1 = String(certifiedData.budget || certifiedData.allocatedBudget || '').trim();
  const bud2 = String(currentData.budget || currentData.allocatedBudget || '').trim();
  if (bud1 && bud2 && bud1 !== bud2) {
    diffs.push({
      tab: 'Tab 1: Brief & Goals',
      field: 'Allocated Budget',
      before: bud1,
      after: bud2,
    });
  }

  const p1 = Array.from(
    new Set((certifiedData.targetPersonas || []).map((p) => String(p).trim()))
  ).sort();
  const p2 = Array.from(
    new Set((currentData.targetPersonas || []).map((p) => String(p).trim()))
  ).sort();
  if (JSON.stringify(p1) !== JSON.stringify(p2)) {
    diffs.push({
      tab: 'Tab 1: Brief & Goals',
      field: 'Target Personas',
      before: p1.join(', '),
      after: p2.join(', '),
    });
  }

  // Tab 2 checks
  const leads1 = (certifiedData.importedLeads || certifiedData.targetLeads || []).length;
  const leads2 = (currentData.importedLeads || currentData.targetLeads || []).length;
  if (leads1 !== leads2) {
    diffs.push({
      tab: 'Tab 2: Target Cohort',
      field: 'Target Leads Count',
      before: `${leads1} leads`,
      after: `${leads2} leads`,
    });
  }

  // Tab 3 checks (Matrix)
  const mat1 = certifiedData.matrix || certifiedData.strategy?.matrix || [];
  const mat2 = currentData.matrix || currentData.strategy?.matrix || [];
  const mat1Simple = mat1.map((m) => ({
    persona: m.persona,
    painPoint: m.painPoint,
    valueProp: m.valueProp,
  }));
  const mat2Simple = mat2.map((m) => ({
    persona: m.persona,
    painPoint: m.painPoint,
    valueProp: m.valueProp,
  }));
  if (JSON.stringify(mat1Simple) !== JSON.stringify(mat2Simple)) {
    if (mat1.length !== mat2.length) {
      diffs.push({
        tab: 'Tab 3: Offer & Matrix',
        field: 'Persona Cards Count',
        before: `${mat1.length} personas`,
        after: `${mat2.length} personas`,
      });
    } else {
      diffs.push({
        tab: 'Tab 3: Offer & Matrix',
        field: 'Messaging & Value Propositions',
        before: 'Original baseline matrix',
        after: 'Customized messaging matrix',
      });
    }
  }

  // Tab 4 checks (Cadence)
  const jrn1 = certifiedData.journey || certifiedData.strategy?.journey || [];
  const jrn2 = currentData.journey || currentData.strategy?.journey || [];
  const jrn1Simple = jrn1.map((j) => ({
    step: j.step,
    day: j.day,
    channel: j.channel,
    name: j.name,
  }));
  const jrn2Simple = jrn2.map((j) => ({
    step: j.step,
    day: j.day,
    channel: j.channel,
    name: j.name,
  }));
  if (JSON.stringify(jrn1Simple) !== JSON.stringify(jrn2Simple)) {
    if (jrn1.length !== jrn2.length) {
      diffs.push({
        tab: 'Tab 4: Outreach Cadence',
        field: 'Journey Touchpoints Count',
        before: `${jrn1.length} touchpoints`,
        after: `${jrn2.length} touchpoints`,
      });
    } else {
      diffs.push({
        tab: 'Tab 4: Outreach Cadence',
        field: 'Cadence Sequence Steps',
        before: 'Original sequence timing & channels',
        after: 'Customized sequence configuration',
      });
    }
  }

  // Tab 6 checks (Gates)
  const gates1 = Array.from(
    new Set(Array.from(certifiedData.governance?.gates || certifiedData.approvedGates || []))
  ).sort();
  const gates2 = Array.from(
    new Set(
      Array.from(customGates || currentData.governance?.gates || currentData.approvedGates || [])
    )
  ).sort();
  if (JSON.stringify(gates1) !== JSON.stringify(gates2)) {
    if (gates1.length !== gates2.length) {
      diffs.push({
        tab: 'Tab 6: Governance',
        field: 'Compliance Gates Count',
        before: `${gates1.length} gates`,
        after: `${gates2.length} gates`,
      });
    } else {
      diffs.push({
        tab: 'Tab 6: Governance',
        field: 'Approved Compliance Gates',
        before: gates1.join(', ') || 'None',
        after: gates2.join(', ') || 'None',
      });
    }
  }

  return diffs;
}
